"use strict";
/**
 *
 * FormMaskedField/useFormMaskedField
 *
 */
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
const react_1 = require("react");
const FormField_1 = require("../FormField");
const textMask_1 = require("./textMask");
function valueToString(value, decimalSymbol) {
    if (decimalSymbol && typeof value === 'number') {
        return String(value).replace('.', decimalSymbol);
    }
    return !value && value !== 0 ? '' : String(value);
}
function getConformedValue(mask, value, decimalSymbol, guide, placeholderChar, previousConformedValue) {
    return !value && value !== 0
        ? ''
        : textMask_1.conformToMask(mask, valueToString(value, decimalSymbol), {
            guide,
            placeholderChar,
            previousConformedValue,
        });
}
function removeCharByIndex(str, index) {
    return str.substring(0, index) + str.substring(index + 1, str.length);
}
function useFormMaskedField(props) {
    const { mask, guide, placeholderChar, keepCharPositions, showMask, disableUnmask, parseNumber, numberDecimalSymbol, required, isAllowed } = props, formFieldProps = __rest(props, ["mask", "guide", "placeholderChar", "keepCharPositions", "showMask", "disableUnmask", "parseNumber", "numberDecimalSymbol", "required", "isAllowed"]);
    const formField = FormField_1.useFormField(formFieldProps);
    const [conformedValue, setConformedValue] = react_1.useState(() => getConformedValue(mask, formField.value, numberDecimalSymbol, guide, placeholderChar));
    const [inputEl, setInputEl] = react_1.useState(null);
    const updateInputEl = react_1.useCallback((el) => {
        if (el !== inputEl) {
            setInputEl(el);
        }
    }, [inputEl]);
    const { validityMessage } = formField.state;
    react_1.useEffect(() => {
        if (inputEl) {
            inputEl.setCustomValidity(validityMessage || '');
            if (validityMessage) {
                inputEl.reportValidity();
            }
        }
    }, [validityMessage || '']);
    const textMaskInput = react_1.useMemo(() => {
        if (inputEl) {
            const instance = textMask_1.createTextMaskInput({
                inputElement: inputEl,
                mask,
                guide,
                placeholderChar,
                keepCharPositions,
                showMask,
            });
            instance.update(inputEl.value);
            return instance;
        }
        return null;
    }, [inputEl, mask, guide, placeholderChar, keepCharPositions, showMask]);
    // check if the value changed by an outside effect, if so update the text mask input
    react_1.useEffect(() => {
        if (textMaskInput) {
            let currConformedValue = conformedValue;
            // find out the suffix
            let suffix;
            const maskItems = typeof mask === 'function' ? mask(currConformedValue) : mask;
            const lastMaskItem = maskItems[maskItems.length - 1];
            if (typeof lastMaskItem === 'string' && lastMaskItem) {
                suffix = lastMaskItem;
            }
            // remove the last character if it is the `numberDecimalSymbol`
            let possibleDecimalIndex = currConformedValue.length - 1;
            if (suffix && currConformedValue[possibleDecimalIndex] === suffix) {
                possibleDecimalIndex--;
            }
            if (currConformedValue[possibleDecimalIndex] === numberDecimalSymbol) {
                currConformedValue = removeCharByIndex(currConformedValue, possibleDecimalIndex);
            }
            const nextConformedValue = getConformedValue(mask, formField.value, numberDecimalSymbol, guide, placeholderChar, currConformedValue);
            if (currConformedValue !== nextConformedValue) {
                textMaskInput.update(valueToString(formField.value, numberDecimalSymbol));
                setConformedValue(nextConformedValue);
            }
        }
    }, [textMaskInput, formField.value, conformedValue, numberDecimalSymbol]);
    const handleChange = react_1.useCallback(({ currentTarget }) => {
        if (textMaskInput) {
            textMaskInput.update(currentTarget.value);
        }
        let nextValue;
        if (disableUnmask) {
            nextValue = currentTarget.value
                ? parseNumber
                    ? parseFloat(currentTarget.value)
                    : currentTarget.value
                : null;
        }
        else {
            const unmaskedValue = textMask_1.unmask(mask, currentTarget.value, numberDecimalSymbol);
            nextValue = unmaskedValue
                ? parseNumber
                    ? parseFloat(unmaskedValue)
                    : unmaskedValue
                : null;
        }
        // update field value only if it is allowed
        if (!isAllowed || isAllowed(nextValue)) {
            formField.setValue(nextValue);
        }
        // we want this asynchronously called because some browsers (safari) decide to hide the validity box on input
        const { validity } = currentTarget;
        if (!validity.valid) {
            const reportValidity = () => currentTarget.reportValidity();
            setTimeout(reportValidity, 0);
        }
        setConformedValue(currentTarget.value);
    }, [formField.setValue, textMaskInput, disableUnmask, parseNumber, numberDecimalSymbol, isAllowed]);
    return Object.assign(Object.assign({}, formField), { inputProps: {
            type: 'text',
            value: conformedValue,
            required,
            disabled: formField.state.disabled,
            readOnly: formField.state.readOnly,
            ref: updateInputEl,
            onChange: handleChange,
        } });
}
exports.useFormMaskedField = useFormMaskedField;
